import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { defaultConfig, getManifest, manifests, resolveConfig, type GameConfig } from '@senses/games'

import { LevelLadder, ParamForm } from '../components/ParamForm.tsx'
import { Alert, Spinner } from '../components/ui.tsx'
import { api } from '../lib/api.ts'
import { useAction, useAsync } from '../lib/hooks.ts'

/**
 * Založení i úprava programu. Formulář parametrů se skládá z manifestu hry,
 * takže nová hra je tady k dispozici okamžitě a bez zásahu do této stránky.
 */
export function ProgramEditPage(): React.JSX.Element {
  const { id } = useParams()
  const [search] = useSearchParams()
  const navigate = useNavigate()

  const isNew = id === undefined
  const programId = Number(id)

  const existing = useAsync(
    async () => (isNew ? null : api.programs.get(programId)),
    [programId, isNew],
  )

  const [gameId, setGameId] = useState(search.get('game') ?? manifests[0]?.id ?? '')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [params, setParams] = useState<GameConfig>(() => {
    const manifest = getManifest(search.get('game') ?? manifests[0]?.id ?? '')
    return manifest ? defaultConfig(manifest) : {}
  })

  // Po načtení existujícího programu se formulář naplní uloženými hodnotami.
  useEffect(() => {
    const program = existing.data?.program
    if (!program) return

    setGameId(program.gameId)
    setName(program.name)
    setDescription(program.description ?? '')

    const manifest = getManifest(program.gameId)
    setParams(manifest ? resolveConfig(manifest, program.params) : {})
  }, [existing.data])

  const manifest = getManifest(gameId)

  /** Změna hry vyžaduje nové výchozí parametry — ty staré k ní nepatří. */
  const changeGame = (nextGameId: string): void => {
    setGameId(nextGameId)
    const next = getManifest(nextGameId)
    setParams(next ? defaultConfig(next) : {})
  }

  const save = useAction(async () => {
    const payload = { name, description: description || null, gameId, params }
    return isNew ? api.programs.create(payload) : api.programs.update(programId, payload)
  })

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    const result = await save.run()
    if (result) void navigate('/programy')
  }

  if (!isNew && existing.loading) return <Spinner />

  return (
    <main className="page">
      <div className="page__header">
        <div>
          <h1>{isNew ? 'Nový program' : 'Úprava programu'}</h1>
          <p>Program je konkrétní hra s konkrétním nastavením obtížnosti.</p>
        </div>
      </div>

      {existing.error && <Alert>{existing.error}</Alert>}

      <form onSubmit={(e) => void submit(e)}>
        {save.error && <Alert>{save.error}</Alert>}

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <label className="field">
            <span className="field__label">Hra</span>
            <select value={gameId} onChange={(e) => changeGame(e.target.value)} disabled={!isNew}>
              {manifests.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.icon} {item.name}
                </option>
              ))}
              {!manifest && <option value={gameId}>{gameId} (neznámá hra)</option>}
            </select>
            {!isNew && (
              <span className="field__help">
                Hru u existujícího programu nelze změnit — nastavení by k ní nesedělo.
                Založ raději nový program.
              </span>
            )}
          </label>

          {manifest && <p className="field__help">{manifest.description}</p>}

          <label className="field">
            <span className="field__label">Název programu</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Např. MOT — Standard (4 cíle)"
              required
              minLength={2}
            />
          </label>

          <label className="field">
            <span className="field__label">Popis</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Pro koho je určený a kdy ho nasazovat."
            />
          </label>
        </div>

        {manifest ? (
          <div className="card">
            <h2 style={{ marginBottom: '1.25rem' }}>Nastavení obtížnosti</h2>
            <ParamForm manifest={manifest} value={params} onChange={setParams} />

            {/* Přepočítává se při každé změně, takže je hned vidět,
                co úprava rychlosti nebo kroku udělá s levely. */}
            <LevelLadder manifest={manifest} config={params} />
          </div>
        ) : (
          <Alert kind="info">
            Hra <code>{gameId}</code> není v této verzi aplikace k dispozici, parametry proto nelze
            editovat.
          </Alert>
        )}

        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1.5rem' }}>
          <button type="button" className="btn" onClick={() => void navigate('/programy')}>
            Zrušit
          </button>
          <button type="submit" className="btn btn--primary" disabled={save.pending}>
            {save.pending ? 'Ukládám…' : isNew ? 'Založit program' : 'Uložit změny'}
          </button>
        </div>
      </form>
    </main>
  )
}
